/**
 * Enhanced Product Gallery with Navigation
 *
 * Features:
 * - Main image navigation with prev/next buttons
 * - Thumbnail slider with navigation buttons
 * - Keyboard navigation (arrow keys)
 * - Touch/swipe support for mobile
 * - Responsive design
 * - Smooth transitions
 * - Auto-scroll thumbnails to keep active image visible
 * - Variant image support
 */
class ProductGallery {
  constructor() {
    this.mainImage = document.getElementById('MainProductImage');
    this.thumbnails = document.querySelectorAll('.product-gallery__thumbnail');
    this.prevButton = document.querySelector('.product-gallery__nav--prev');
    this.nextButton = document.querySelector('.product-gallery__nav--next');
    this.thumbPrevButton = document.querySelector(
      '.product-gallery__thumb-nav--prev',
    );
    this.thumbNextButton = document.querySelector(
      '.product-gallery__thumb-nav--next',
    );
    this.thumbnailsWrapper = document.querySelector(
      '.product-gallery__thumbnails-wrapper',
    );
    this.thumbnailsContainer = document.querySelector(
      '.product-gallery__thumbnails-container',
    );

    this.isTransitioning = false;
    this.currentIndex = 0;
    this.thumbnailsPerView = 4;
    this.currentThumbnailOffset = 0;

    if (!this.mainImage || !this.thumbnails.length) return;

    this.init();
  }

  init() {
    this.setupThumbnails();
    this.setupMainNavigation();
    this.setupThumbnailNavigation();
    this.setupResizeHandler();
    this.updateThumbnailNavigation();
    this.preloadImages();
  }

  setupResizeHandler() {
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.currentThumbnailOffset = 0;
        if (this.thumbnailsWrapper) {
          this.thumbnailsWrapper.style.transform = 'translateX(0px)';
        }
        this.updateThumbnailNavigation();
      }, 250);
    });
  }

  setupThumbnails() {
    this.thumbnails.forEach((thumbnail, index) => {
      // Handle hover events for desktop
      thumbnail.addEventListener('mouseenter', () => {
        if (!this.isMobile()) {
          this.goToImage(index);
        }
      });

      // Handle click events for mobile and desktop
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToImage(index);
      });

      // Handle keyboard navigation
      thumbnail.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.goToImage(index);
        }

        // Arrow key navigation
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          this.navigateWithKeys(e.key, index);
        }
      });
    });
  }

  setupMainNavigation() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.goToPrevious();
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.goToNext();
      });
    }

    // Keyboard navigation for main image
    if (this.mainImage) {
      this.mainImage.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.goToPrevious();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.goToNext();
        }
      });
    }

    // Touch/swipe support for main image
    this.setupTouchNavigation();
  }

  setupTouchNavigation() {
    if (!this.mainImage) return;

    let startX = 0;
    let startY = 0;
    let isSwipe = false;

    this.mainImage.addEventListener(
      'touchstart',
      (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isSwipe = false;
      },
      { passive: true },
    );

    this.mainImage.addEventListener(
      'touchmove',
      (e) => {
        if (!startX || !startY) return;

        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const diffX = Math.abs(currentX - startX);
        const diffY = Math.abs(currentY - startY);

        // If horizontal swipe is more significant than vertical
        if (diffX > diffY && diffX > 30) {
          isSwipe = true;
          e.preventDefault(); // Prevent scrolling
        }
      },
      { passive: false },
    );

    this.mainImage.addEventListener(
      'touchend',
      (e) => {
        if (!startX || !isSwipe) return;

        const endX = e.changedTouches[0].clientX;
        const diffX = startX - endX;

        // Minimum swipe distance
        if (Math.abs(diffX) > 50) {
          if (diffX > 0) {
            // Swiped left - go to next
            this.goToNext();
          } else {
            // Swiped right - go to previous
            this.goToPrevious();
          }
        }

        startX = 0;
        startY = 0;
        isSwipe = false;
      },
      { passive: true },
    );
  }

  setupThumbnailNavigation() {
    if (this.thumbPrevButton) {
      this.thumbPrevButton.addEventListener('click', () => {
        this.scrollThumbnails('prev');
      });
    }

    if (this.thumbNextButton) {
      this.thumbNextButton.addEventListener('click', () => {
        this.scrollThumbnails('next');
      });
    }
  }

  goToImage(index) {
    if (index < 0 || index >= this.thumbnails.length || this.isTransitioning)
      return;

    this.currentIndex = index;
    const thumbnail = this.thumbnails[index];

    this.setActiveThumb(thumbnail);
    this.updateMainImage(thumbnail);
    this.updateMainImageIndex();
    this.ensureThumbnailVisible(index);
  }

  goToPrevious() {
    const newIndex =
      this.currentIndex > 0
        ? this.currentIndex - 1
        : this.thumbnails.length - 1;
    this.goToImage(newIndex);
  }

  goToNext() {
    const newIndex =
      this.currentIndex < this.thumbnails.length - 1
        ? this.currentIndex + 1
        : 0;
    this.goToImage(newIndex);
  }

  scrollThumbnails(direction) {
    if (!this.thumbnailsWrapper || !this.thumbnailsContainer) return;

    const thumbnailWidth = this.getThumbnailWidth();
    const containerWidth = this.thumbnailsContainer.offsetWidth;
    const maxOffset = Math.max(
      0,
      this.thumbnails.length * thumbnailWidth - containerWidth,
    );

    if (direction === 'prev') {
      this.currentThumbnailOffset = Math.max(
        0,
        this.currentThumbnailOffset - thumbnailWidth * this.thumbnailsPerView,
      );
    } else {
      this.currentThumbnailOffset = Math.min(
        maxOffset,
        this.currentThumbnailOffset + thumbnailWidth * this.thumbnailsPerView,
      );
    }

    this.thumbnailsWrapper.style.transform = `translateX(-${this.currentThumbnailOffset}px)`;
    this.updateThumbnailNavigation();
  }

  getThumbnailWidth() {
    // Get actual thumbnail width including gap
    if (this.thumbnails.length > 0) {
      const thumbnail = this.thumbnails[0];
      const computedStyle = window.getComputedStyle(thumbnail);
      const width = thumbnail.offsetWidth;
      const marginRight = parseFloat(computedStyle.marginRight) || 0;
      const gap =
        parseFloat(window.getComputedStyle(this.thumbnailsWrapper).gap) || 8;
      return width + gap;
    }
    return this.isMobile() ? 68 : 88; // fallback: thumbnail width + gap
  }

  ensureThumbnailVisible(index) {
    if (!this.thumbnailsWrapper || !this.thumbnailsContainer) return;

    const thumbnailWidth = this.getThumbnailWidth();
    const containerWidth = this.thumbnailsContainer.offsetWidth;
    const thumbnailPosition = index * thumbnailWidth;
    const visibleStart = this.currentThumbnailOffset;
    const visibleEnd = this.currentThumbnailOffset + containerWidth;

    // If thumbnail is not visible, scroll to make it visible
    if (thumbnailPosition < visibleStart) {
      this.currentThumbnailOffset = Math.max(
        0,
        thumbnailPosition - thumbnailWidth,
      );
    } else if (thumbnailPosition + thumbnailWidth > visibleEnd) {
      this.currentThumbnailOffset = Math.min(
        this.thumbnails.length * thumbnailWidth - containerWidth,
        thumbnailPosition - containerWidth + thumbnailWidth * 2,
      );
    }

    this.thumbnailsWrapper.style.transform = `translateX(-${this.currentThumbnailOffset}px)`;
    this.updateThumbnailNavigation();
  }

  updateThumbnailNavigation() {
    if (
      !this.thumbPrevButton ||
      !this.thumbNextButton ||
      !this.thumbnailsContainer
    )
      return;

    const thumbnailWidth = this.getThumbnailWidth();
    const containerWidth = this.thumbnailsContainer.offsetWidth;
    const maxOffset = Math.max(
      0,
      this.thumbnails.length * thumbnailWidth - containerWidth,
    );

    // Update button states
    this.thumbPrevButton.disabled = this.currentThumbnailOffset <= 0;
    this.thumbNextButton.disabled = this.currentThumbnailOffset >= maxOffset;

    // Hide buttons if all thumbnails fit
    if (maxOffset <= 0) {
      this.thumbPrevButton.style.display = 'none';
      this.thumbNextButton.style.display = 'none';
    } else {
      this.thumbPrevButton.style.display = 'flex';
      this.thumbNextButton.style.display = 'flex';
    }
  }

  updateMainImageIndex() {
    if (this.mainImage) {
      this.mainImage.setAttribute('data-current-index', this.currentIndex);
    }
  }

  updateMainImage(thumbnail) {
    if (this.isTransitioning) return;

    const newSrc = thumbnail.dataset.imageSrc;
    const newAlt = thumbnail.dataset.imageAlt;

    if (newSrc && !this.mainImage.src.includes(newSrc)) {
      this.isTransitioning = true;

      // Create new image element for preloading
      const newImage = new Image();

      newImage.onload = () => {
        // Add loading state
        this.mainImage.classList.add('loading');

        // Update image after short delay for smooth transition
        setTimeout(() => {
          this.mainImage.src = newSrc;
          this.mainImage.alt = newAlt;
          this.mainImage.classList.remove('loading');
          this.isTransitioning = false;
        }, 100);
      };

      newImage.onerror = () => {
        this.isTransitioning = false;
      };

      newImage.src = newSrc;
    }
  }

  setActiveThumb(activeThumb) {
    // Remove active class from all thumbnails
    this.thumbnails.forEach((thumb) => {
      thumb.classList.remove('active');
    });

    // Add active class to current thumbnail
    activeThumb.classList.add('active');
  }

  navigateWithKeys(direction, currentIndex) {
    let newIndex;

    if (direction === 'ArrowLeft') {
      newIndex =
        currentIndex > 0 ? currentIndex - 1 : this.thumbnails.length - 1;
    } else {
      newIndex =
        currentIndex < this.thumbnails.length - 1 ? currentIndex + 1 : 0;
    }

    this.goToImage(newIndex);
    this.thumbnails[newIndex].focus();
  }

  preloadImages() {
    this.thumbnails.forEach((thumbnail) => {
      const imageSrc = thumbnail.dataset.imageSrc;
      if (imageSrc) {
        const img = new Image();
        img.src = imageSrc;
      }
    });
  }

  isMobile() {
    return window.innerWidth <= 768;
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new ProductGallery();
});

// Re-initialize if content changes (for theme editor)
document.addEventListener('shopify:section:load', () => {
  new ProductGallery();
});

// Handle variant changes (if using variant images)
document.addEventListener('variant:change', (event) => {
  const gallery = new ProductGallery();
  if (
    event.detail &&
    event.detail.variant &&
    event.detail.variant.featured_image
  ) {
    // Find and activate the variant image
    const variantImageId = event.detail.variant.featured_image.id;
    const thumbnails = document.querySelectorAll('.product-gallery__thumbnail');

    thumbnails.forEach((thumbnail, index) => {
      const img = thumbnail.querySelector('img');
      if (img && img.src.includes(variantImageId)) {
        gallery.goToImage(index);
      }
    });
  }
});
